// Chakra imports
import {Box, Portal, useDisclosure} from "components-lib";
// Layout components
import Navbar from "components/navbar/NavbarAdmin.js";
import SimpleNavbar from "components/navbar/SimpleNavbar.js";
import Sidebar from "components/sidebar/Sidebar.js";
import {SidebarContext} from "contexts/SidebarContext";
import {GlobalFiltersProvider} from "contexts/GlobalFiltersContext";
import {FocusModeProvider} from "contexts/FocusModeContext";
import React, {useState} from "react";
import {Navigate, Route, Routes} from "react-router-dom";
import routes from "routes.js";

// Utility: filter routes by role
function filterRoutesByRole(routes, role) {
    if (role === "front_desk" || role === "frontdesk") {
        // Only allow the Front Desk Dashboard route for frontdesk users
        return routes.filter((r) => r.name === "Front Desk Dashboard");
    }
    if (role === "doctor" || role === "" || role === undefined) {
        // For doctors (including those with empty role), only show the Doctors page
        return routes.filter((r) => r.name === "Doctors");
    }
    // For admin or other roles, return all routes
    return routes;
}

// Custom Chakra theme
export default function Dashboard(props) {
    const {...rest} = props;
    // states and functions
    const [fixed] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Get user role and data from localStorage
    const tokenPayload = JSON.parse(localStorage.getItem("tokenPayload") || "{}");
    const userRole = tokenPayload?.role?.toLowerCase();
    const isAdmin = userRole === "admin";
    const isDoctor =
        userRole === "doctor" || userRole === "" || userRole === undefined;

    // Filter routes by role
    // const filteredRoutes = filterRoutesByRole(routes, userRole);
    const filteredRoutes = routes;

    // Determine if sidebar should be shown (for admin and doctors)
    const showSidebar = isAdmin;
    const SIDEBAR_WIDTH = 280;
    const sidebarOffset = showSidebar
        ? isSidebarCollapsed
            ? 0
            : SIDEBAR_WIDTH
        : 0;
    const handleSidebarCollapse = () => setIsSidebarCollapsed((prev) => !prev);
    // functions for changing the states from components
    const getRoute = () => {
        return window.location.pathname !== "/admin/full-screen-maps";
    };
    const getActiveRoute = (routes) => {
        let activeRoute = "Default Brand Text";
        for (let i = 0; i < routes.length; i++) {
            if (routes[i].collapse) {
                let collapseActiveRoute = getActiveRoute(routes[i].items);
                if (collapseActiveRoute !== activeRoute) {
                    return collapseActiveRoute;
                }
            } else if (routes[i].category) {
                let categoryActiveRoute = getActiveRoute(routes[i].items);
                if (categoryActiveRoute !== activeRoute) {
                    return categoryActiveRoute;
                }
            } else {
                const fullPath = routes[i].layout + routes[i].path;
                // Prefer exact match
                if (window.location.pathname === fullPath) {
                    return routes[i].name;
                }
                // Fallback to partial match
                if (window.location.href.indexOf(fullPath) !== -1) {
                    activeRoute = routes[i].name;
                }
            }
        }
        return activeRoute;
    };
    const getActiveNavbar = (routes) => {
        let activeNavbar = false;
        for (let i = 0; i < routes.length; i++) {
            if (routes[i].collapse) {
                let collapseActiveNavbar = getActiveNavbar(routes[i].items);
                if (collapseActiveNavbar !== activeNavbar) {
                    return collapseActiveNavbar;
                }
            } else if (routes[i].category) {
                let categoryActiveNavbar = getActiveNavbar(routes[i].items);
                if (categoryActiveNavbar !== activeNavbar) {
                    return categoryActiveNavbar;
                }
            } else {
                if (
                    window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
                ) {
                    return routes[i].secondary;
                }
            }
        }
        return activeNavbar;
    };
    const getActiveNavbarText = (routes) => {
        let activeNavbar = false;
        for (let i = 0; i < routes.length; i++) {
            if (routes[i].collapse) {
                let collapseActiveNavbar = getActiveNavbarText(routes[i].items);
                if (collapseActiveNavbar !== activeNavbar) {
                    return collapseActiveNavbar;
                }
            } else if (routes[i].category) {
                let categoryActiveNavbar = getActiveNavbarText(routes[i].items);
                if (categoryActiveNavbar !== activeNavbar) {
                    return categoryActiveNavbar;
                }
            } else {
                if (
                    window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
                ) {
                    return routes[i].messageNavbar;
                }
            }
        }
        return activeNavbar;
    };
    const getRoutes = (routes) => {
        return routes.map((prop, key) => {
            if (prop.layout === "/admin") {
                return (
                    <Route
                        path={prop.path}
                        element={<prop.component/>}
                        key={key}
                    />
                );
            }
            if (prop.collapse) {
                return getRoutes(prop.items);
            }
            if (prop.category) {
                return getRoutes(prop.items);
            } else {
                return null;
            }
        });
    };
    document.documentElement.dir = "ltr";
    const {onOpen} = useDisclosure();
    document.documentElement.dir = "ltr";
    return (
        <GlobalFiltersProvider>
            <FocusModeProvider>
                <SidebarContext.Provider
                    value={{
                        isSidebarCollapsed,
                        setIsSidebarCollapsed,
                        toggleSidebar: isSidebarCollapsed,
                        setToggleSidebar: setIsSidebarCollapsed,
                    }}
                >
                    <Box display="flex" minH="100vh" w="100%">
                        {/* Conditionally render sidebar only for admin users */}
                        {showSidebar && (
                            <Sidebar
                                routes={filteredRoutes}
                                sidebarWidth={SIDEBAR_WIDTH}
                                isCollapsed={isSidebarCollapsed}
                                onToggleCollapse={handleSidebarCollapse}
                            />
                        )}

                        {/* Main content area - full width for non-admin, with sidebar space for admin */}
                        <Box
                            as="main"
                            id="main-content"
                            minHeight="100vh"
                            height="100%"
                            overflow="auto"
                            position="relative"
                            maxHeight="100%"
                            flex="1"
                            w="100%"
                            maxWidth="100%"
                            transition="margin 0.3s ease"
                            ml={{base: "0px", xl: showSidebar ? `${sidebarOffset}px` : "0px"}}
                            marginRight="0px"
                        >


                            {/* Render appropriate navbar based on user role */}
                            {showSidebar ? (
                                <Portal>
                                    <Box>
                                        <Navbar
                                            onOpen={onOpen}
                                            logoText={""}
                                            brandText={getActiveRoute(filteredRoutes)}
                                            secondary={getActiveNavbar(filteredRoutes)}
                                            message={getActiveNavbarText(filteredRoutes)}
                                            fixed={fixed}
                                            sidebarOffset={sidebarOffset}
                                            routes={filteredRoutes}
                                            {...rest}
                                        />
                                    </Box>
                                </Portal>
                            ) : (
                                <SimpleNavbar
                                    brandText={getActiveRoute(filteredRoutes)}
                                    userRole={userRole}
                                    routes={filteredRoutes}
                                />
                            )}

                            {getRoute() ? (
                                <Box
                                    mx="auto"
                                    p={
                                        showSidebar
                                            ? {base: "20px", md: "30px"}
                                            : {base: "0px", md: "0px"}
                                    }
                                    pe={showSidebar ? "20px" : "0px"}
                                    minH="100vh"
                                    pt={showSidebar ? "50px" : "120px"}
                                >
                                    <Box className="admin-routes-container">
                                        <Routes>
                                            {getRoutes(filteredRoutes)}
                                            {userRole === "front_desk" && (
                                                <Route path="*" element={<Navigate to="/admin/frontdesk" replace/>}/>
                                            )}
                                            {userRole === "doctor" && (
                                                <Route path="*" element={<Navigate to="/admin/doctor" replace/>}/>
                                            )}
                                            {(!userRole ||
                                                (userRole !== "front_desk" && userRole !== "doctor")) && (
                                                <Route path="*" element={<Navigate to="/admin/default" replace/>}/>
                                            )}
                                        </Routes>
                                    </Box>
                                </Box>
                            ) : null}

                        </Box>
                    </Box>
                </SidebarContext.Provider>
            </FocusModeProvider>
        </GlobalFiltersProvider>
    );
}
